import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { storesAPI } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Building2, MapPin, TrendingUp, Users, Upload, Plus, Search, Lock, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export default function StoresPage() {
  const navigate = useNavigate();
  const [stores, setStores] = useState([]);
  const [filteredStores, setFilteredStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regionFilter, setRegionFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [newStore, setNewStore] = useState({
    name: '',
    store_code: '',
    city: '',
    region: '',
    address: '',
    segment_tags: ['B'],
    traffic_score: 70,
    priority_score: 50
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    loadStores();
  }, []);

  useEffect(() => {
    filterStores();
  }, [regionFilter, searchTerm, stores]);

  const loadStores = async () => {
    try {
      const response = await storesAPI.getAll();
      setStores(response.data);
      setFilteredStores(response.data);
    } catch (error) {
      toast.error('Error al cargar tiendas');
    } finally {
      setLoading(false);
    }
  };

  const filterStores = () => {
    let filtered = [...stores];

    if (regionFilter !== 'all') {
      filtered = filtered.filter(s => s.region === regionFilter);
    }

    if (searchTerm) {
      filtered = filtered.filter(s => 
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.city.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.store_code.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredStores(filtered);
  };

  const handleCreateStore = async () => {
    if (!newStore.name || !newStore.store_code || !newStore.city || !newStore.region) {
      toast.error('Por favor completa los campos obligatorios');
      return;
    }
    
    setCreating(true);
    try {
      await storesAPI.create(newStore);
      toast.success('Farmacia creada exitosamente');
      setShowCreateDialog(false);
      setNewStore({
        name: '',
        store_code: '',
        city: '',
        region: '',
        address: '',
        segment_tags: ['B'],
        traffic_score: 70,
        priority_score: 50
      });
      loadStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear farmacia');
    } finally {
      setCreating(false);
    }
  };

  const regions = [...new Set(stores.map(s => s.region))];
  
  // Group stores by region for stats
  const storesByRegion = regions.map(region => ({
    region,
    count: stores.filter(s => s.region === region).length,
    avgTraffic: Math.round(
      stores.filter(s => s.region === region).reduce((acc, s) => acc + s.traffic_score, 0) /
      stores.filter(s => s.region === region).length
    )
  }));

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div data-testid="stores-page">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Farmacias</h1>
          <p className="text-lg text-slate-600">Red de farmacias en Ecuador - {stores.length} ubicaciones</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline" 
            onClick={() => navigate('/availability-blocks')} 
            data-testid="manage-blocks-btn"
            className="border-amber-300 text-amber-700 hover:bg-amber-50"
          >
            <Lock className="w-4 h-4 mr-2" />
            Gestionar Bloqueos
          </Button>
          <Button variant="outline" onClick={() => setShowImportDialog(true)} data-testid="import-stores-btn">
            <Upload className="w-4 h-4 mr-2" />
            Importar Excel
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-store-btn">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Farmacia
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card className="border-2 border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium mb-1">Total Farmacias</p>
                <p className="text-3xl font-bold text-primary">{stores.length}</p>
              </div>
              <Building2 className="w-12 h-12 text-primary/30" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-transparent">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium mb-1">Regiones</p>
                <p className="text-3xl font-bold text-emerald-600">{regions.length}</p>
              </div>
              <MapPin className="w-12 h-12 text-emerald-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-transparent">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium mb-1">Tráfico Promedio</p>
                <p className="text-3xl font-bold text-amber-600">
                  {Math.round(stores.reduce((acc, s) => acc + s.traffic_score, 0) / stores.length)}
                </p>
              </div>
              <TrendingUp className="w-12 h-12 text-amber-200" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-transparent">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium mb-1">Segmento A</p>
                <p className="text-3xl font-bold text-blue-600">
                  {stores.filter(s => s.segment_tags.includes('A')).length}
                </p>
              </div>
              <Users className="w-12 h-12 text-blue-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6 border-slate-200">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Buscar por nombre, ciudad o código..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={regionFilter} onValueChange={setRegionFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filtrar por región" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las regiones</SelectItem>
                {regions.map(region => (
                  <SelectItem key={region} value={region}>{region}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Stores by Region */}
      {regions.map(region => {
        const regionStores = filteredStores.filter(s => s.region === region);
        if (regionStores.length === 0) return null;

        const regionStats = storesByRegion.find(r => r.region === region);

        return (
          <div key={region} className="mb-8">
            <div className="flex items-center justify-between mb-4 p-4 bg-gradient-to-r from-primary/5 to-transparent rounded-lg border-l-4 border-primary">
              <div className="flex items-center gap-3">
                <MapPin className="w-6 h-6 text-primary" />
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">{region}</h2>
                  <p className="text-sm text-slate-600">
                    {regionStats.count} farmacias • Tráfico promedio: {regionStats.avgTraffic}
                  </p>
                </div>
              </div>
              <Badge className="bg-primary/10 text-primary border-primary/20 text-base px-4 py-2">
                {regionStores.length}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {regionStores.map((store) => (
                <Card 
                  key={store.id} 
                  className="border-slate-200 hover:shadow-lg hover:border-primary/50 transition-all cursor-pointer group" 
                  data-testid={`store-card-${store.id}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <CardTitle className="text-lg group-hover:text-primary transition-colors">
                          {store.name}
                        </CardTitle>
                        <CardDescription className="text-xs mt-1">
                          <span className="font-mono font-medium">{store.store_code}</span>
                        </CardDescription>
                      </div>
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                        <Building2 className="w-6 h-6 text-primary" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-start gap-2 text-sm text-slate-600">
                      <MapPin className="w-4 h-4 mt-0.5 text-slate-400 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-slate-700">{store.city}</p>
                        <p className="text-xs">{store.address}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {store.segment_tags.map((tag) => (
                        <Badge 
                          key={tag} 
                          variant="outline" 
                          className={`text-xs ${
                            tag === 'A' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' :
                            tag === 'B' ? 'border-blue-300 text-blue-700 bg-blue-50' :
                            'border-amber-300 text-amber-700 bg-amber-50'
                          }`}
                        >
                          Segmento {tag}
                        </Badge>
                      ))}
                    </div>

                    <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                      <div>
                        <p className="text-xs text-slate-500">Tráfico</p>
                        <div className="flex items-center gap-1">
                          <div className={`w-2 h-2 rounded-full ${
                            store.traffic_score >= 85 ? 'bg-emerald-500' :
                            store.traffic_score >= 70 ? 'bg-blue-500' :
                            'bg-amber-500'
                          }`}></div>
                          <span className="text-sm font-bold text-slate-900">{store.traffic_score}</span>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Prioridad</p>
                        <span className="text-sm font-bold text-slate-900">{store.priority_score}</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/availability-blocks?store=${store.id}`);
                        }}
                        className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                        title="Ver bloqueos de esta tienda"
                      >
                        <Calendar className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );
      })}

      {filteredStores.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No se encontraron farmacias con los filtros seleccionados</p>
          </CardContent>
        </Card>
      )}

      {/* Create Store Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nueva Farmacia</DialogTitle>
            <DialogDescription>Agrega una nueva farmacia a la red</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  value={newStore.name}
                  onChange={(e) => setNewStore({...newStore, name: e.target.value})}
                  placeholder="Medicity Centro"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Código *</Label>
                <Input
                  value={newStore.store_code}
                  onChange={(e) => setNewStore({...newStore, store_code: e.target.value})}
                  placeholder="MED-001"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Ciudad *</Label>
                <Input
                  value={newStore.city}
                  onChange={(e) => setNewStore({...newStore, city: e.target.value})}
                  placeholder="Quito"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Región *</Label>
                <Input
                  value={newStore.region}
                  onChange={(e) => setNewStore({...newStore, region: e.target.value})}
                  placeholder="Sierra Norte"
                  className="mt-1"
                />
              </div>
            </div>
            <div>
              <Label>Dirección</Label>
              <Input
                value={newStore.address}
                onChange={(e) => setNewStore({...newStore, address: e.target.value})}
                placeholder="Av. Principal 123"
                className="mt-1"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Segmento</Label>
                <Select
                  value={newStore.segment_tags[0]}
                  onValueChange={(val) => setNewStore({...newStore, segment_tags: [val]})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">Segmento A</SelectItem>
                    <SelectItem value="B">Segmento B</SelectItem>
                    <SelectItem value="C">Segmento C</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tráfico (0-100)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={newStore.traffic_score}
                  onChange={(e) => setNewStore({...newStore, traffic_score: parseInt(e.target.value) || 0})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Prioridad (0-100)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={newStore.priority_score}
                  onChange={(e) => setNewStore({...newStore, priority_score: parseInt(e.target.value) || 0})}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={handleCreateStore} disabled={creating}>
                {creating ? 'Creando...' : 'Crear Farmacia'}
              </Button>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Importar Farmacias desde Excel</DialogTitle>
            <DialogDescription>Sube un archivo Excel con los datos de las farmacias</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Archivo Excel</Label>
              <Input type="file" accept=".xlsx,.xls" className="mt-1" />
            </div>
            <p className="text-sm text-slate-600">
              El archivo debe contener las columnas: nombre, código, ciudad, región, dirección, segmento, tráfico
            </p>
            <div className="flex gap-3">
              <Button onClick={() => toast.info('Función de importación en desarrollo')}>
                Importar
              </Button>
              <Button variant="outline" onClick={() => setShowImportDialog(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
